// Sigma migration readiness check: questions and scoring shared by the page and tests.

export const ANSWERS = Object.freeze({ yes: 2, partly: 1, no: 0 });

export const AREAS = Object.freeze([
  {
    id: 'scope',
    name: 'Scope',
    questions: [
      { id: 'inventory', text: 'Do you have a list of every dashboard and report in the current tool, with an owner for each?', guide: 'which-dashboards-to-migrate-consolidate-or-retire' },
      { id: 'usage', text: 'Can you see which reports are actually opened, and how often?', guide: 'which-dashboards-to-migrate-consolidate-or-retire' },
      { id: 'deadline', text: 'Do you know the renewal date or other deadline the migration has to meet?', guide: 'how-long-does-a-sigma-migration-take' },
      { id: 'sponsor', text: 'Is there a business owner who can approve retiring old reports?', guide: 'what-should-a-sigma-migration-assessment-include' },
    ],
  },
  {
    id: 'data',
    name: 'Data and calculations',
    questions: [
      { id: 'warehouse', text: 'Is the data the reports use already in a cloud warehouse Sigma can connect to?', guide: 'what-should-a-sigma-migration-assessment-include' },
      { id: 'logic', text: 'Do you know which calculations exist only inside the current BI tool?', guide: 'power-bi-tableau-to-sigma-migration-cost' },
      { id: 'definitions', text: 'Are the main metrics defined in writing, so two people would calculate them the same way?', guide: '7-dax-measure-tests-before-migrating-to-sigma' },
      { id: 'security', text: 'Is row-level or user-based access documented, including who should be denied?', guide: 'how-to-validate-a-sigma-migration' },
    ],
  },
  {
    id: 'acceptance',
    name: 'Acceptance',
    questions: [
      { id: 'cases', text: 'Have you listed the totals, segments, and edge cases that must match before sign-off?', guide: 'how-to-validate-a-sigma-migration' },
      { id: 'interactions', text: 'Have you noted the filters, drill paths, and actions users rely on?', guide: '7-tableau-interactions-to-review-before-moving-to-sigma' },
      { id: 'reviewer', text: 'Is there a named reviewer for each report who can say it matches?', guide: 'how-to-validate-a-sigma-migration' },
      { id: 'exports', text: 'Do you know which exports, schedules, and embedded views downstream users depend on?', guide: '10-checks-before-a-sigma-migration-cutover' },
    ],
  },
  {
    id: 'rollout',
    name: 'Rollout',
    questions: [
      { id: 'pilot', text: 'Have you picked a pilot report that includes the hard parts, not just the easiest one?', guide: '8-tests-for-choosing-a-sigma-migration-pilot' },
      { id: 'waves', text: 'Are reports grouped into waves that share data sources?', guide: 'how-long-does-a-sigma-migration-take' },
      { id: 'owner', text: 'Is someone named to support users and own the Sigma workbooks after launch?', guide: '10-checks-before-a-sigma-migration-cutover' },
      { id: 'cutover', text: 'Is there a cutover and rollback plan for switching off the old reports?', guide: '10-checks-before-a-sigma-migration-cutover' },
    ],
  },
]);

export const QUESTION_COUNT = AREAS.reduce((total, area) => total + area.questions.length, 0);

export function score(answers) {
  const safe = answers && typeof answers === 'object' ? answers : {};
  let answered = 0;
  let points = 0;
  const gaps = [];
  const areas = AREAS.map((area) => {
    let areaPoints = 0;
    let areaAnswered = 0;
    for (const question of area.questions) {
      const answer = safe[question.id];
      if (typeof answer !== 'string' || !Object.prototype.hasOwnProperty.call(ANSWERS, answer)) continue;
      areaAnswered += 1;
      areaPoints += ANSWERS[answer];
      if (answer !== 'yes') gaps.push({ ...question, area: area.name, answer });
    }
    answered += areaAnswered;
    points += areaPoints;
    const max = area.questions.length * ANSWERS.yes;
    return { id: area.id, name: area.name, answered: areaAnswered, total: area.questions.length, points: areaPoints, percent: Math.round((areaPoints / max) * 100) };
  });
  const percent = Math.round((points / (QUESTION_COUNT * ANSWERS.yes)) * 100);
  const complete = answered === QUESTION_COUNT;
  return { answered, complete, percent, areas, gaps, band: complete ? bandFor(percent) : null };
}

export function bandFor(percent) {
  if (percent >= 80) {
    return { id: 'pilot', label: 'Ready to plan a pilot', body: 'The groundwork is in place. Pick a representative report, agree its acceptance cases, and use it to test the estimate for the rest.' };
  }
  if (percent >= 50) {
    return { id: 'assess', label: 'Ready for an assessment', body: 'You know enough to scope the work, but the gaps below will change the estimate. Close them during a short assessment before committing to dates.' };
  }
  return { id: 'groundwork', label: 'Start with the groundwork', body: 'An estimate made now would mostly be guesswork. Build the inventory, find where the data and logic live, and name the owners first.' };
}
